/**
 * Types for FSANZ AUSNUT/AFCD-style food composition data.
 * Nutrients are stored per 100g; multiply by (amountGrams/100) for a portion.
 */

/** Nutrients per 100g (AUSNUT/AFCD convention). Optional fields may be missing in some sources. */
export interface NutrientsPer100g {
  /** Energy in kcal. */
  energyKcal: number
  /** Protein in g. */
  proteinG: number
  /** Carbohydrate in g. */
  carbsG: number
  /** Total fat in g. Optional; populated for barcode-scanned products. */
  fatG?: number
  /** Dietary fibre in g. */
  fibreG: number
  /** Calcium in mg. */
  calciumMg: number
  /** Iron in mg. */
  ironMg: number
  /** Water in g (for hydration estimate; 1 g ≈ 1 mL). */
  waterG: number
  /** Zinc in mg. Optional; when missing, show "data not available". */
  zincMg?: number
  /** Magnesium in mg. */
  magnesiumMg?: number
  /** Omega-3 fatty acids in g. */
  omega3G?: number
  /** Vitamin D in µg. */
  vitaminDUg?: number
  /** Sodium in mg. */
  sodiumMg?: number
}

/** One food in the database (id + name + per-100g nutrients). */
export interface FSANZFood {
  /** Unique id (e.g. AUSNUT food code or internal id). */
  id: string
  /** Food name as in AUSNUT/AFCD (e.g. "Banana, raw"). */
  name: string
  /** Optional short name for display. */
  shortName?: string
  /** Nutrients per 100 g. */
  per100g: NutrientsPer100g
}

/** Same shape as Nutritionix MealNutrients so callers can use one type. Optional nutrients are null when data not available. */
export interface MealNutrients {
  calories: number
  carbs: number
  protein: number
  fibre: number
  calcium: number
  iron: number
  hydration: number
  /** null when source has no data for this nutrient. */
  zinc?: number | null
  magnesium?: number | null
  omega3?: number | null
  vitaminD?: number | null
  sodium?: number | null
}

/** Scale per-100g nutrients to a given amount in grams. Optional nutrients become null when not in per100g. */
export function nutrientsForAmount(per100g: NutrientsPer100g, amountGrams: number): MealNutrients {
  const scale = amountGrams / 100
  return {
    calories: Math.round(per100g.energyKcal * scale),
    protein: Math.round(per100g.proteinG * scale * 10) / 10,
    carbs: Math.round(per100g.carbsG * scale * 10) / 10,
    fibre: Math.round(per100g.fibreG * scale * 10) / 10,
    calcium: Math.round(per100g.calciumMg * scale),
    iron: Math.round(per100g.ironMg * scale * 10) / 10,
    hydration: Math.round(per100g.waterG * scale),
    zinc: per100g.zincMg !== undefined && Number.isFinite(per100g.zincMg) ? Math.round(per100g.zincMg * scale * 10) / 10 : null,
    magnesium: per100g.magnesiumMg !== undefined && Number.isFinite(per100g.magnesiumMg) ? Math.round(per100g.magnesiumMg * scale * 10) / 10 : null,
    omega3: per100g.omega3G !== undefined && Number.isFinite(per100g.omega3G) ? Math.round(per100g.omega3G * scale * 100) / 100 : null,
    vitaminD: per100g.vitaminDUg !== undefined && Number.isFinite(per100g.vitaminDUg) ? Math.round(per100g.vitaminDUg * scale * 10) / 10 : null,
    sodium: per100g.sodiumMg !== undefined && Number.isFinite(per100g.sodiumMg) ? Math.round(per100g.sodiumMg * scale) : null,
  }
}
